import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import BlogNavbar from './components/BlogNavbar';
import Footer from '../home/components/Footer';
import SEOHead from '../../components/feature/SEOHead';
import { supabase, BlogPost } from '../../lib/supabase';

export default function BlogPage() {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [filteredPosts, setFilteredPosts] = useState<BlogPost[]>([]);
  const [selectedCategory, setSelectedCategory] = useState('全部');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const categories = ['全部', '重要公告', '活動花絮', '課程公告', '健康資訊'];

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const { data, error: fetchError } = await supabase
        .from('blog_posts')
        .select('*')
        .order('date', { ascending: false });

      if (fetchError) throw fetchError;

      setPosts(data || []);
      setFilteredPosts(data || []);
    } catch (err) {
      console.error('Error fetching posts:', err);
      setError('載入文章時發生錯誤，請稍後再試');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let filtered = posts;

    if (selectedCategory !== '全部') {
      filtered = filtered.filter(post => post.category === selectedCategory);
    }

    if (searchQuery) {
      filtered = filtered.filter(post =>
        post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        post.excerpt.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    setFilteredPosts(filtered);
  }, [selectedCategory, searchQuery, posts]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('zh-TW', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const blogPageSchema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "@id": "https://qianjin-daycare.com/blog/#webpage",
    "url": "https://qianjin-daycare.com/blog",
    "name": "最新消息｜前金日間照顧中心",
    "description": "前金日間照顧中心最新消息與活動花絮，掌握高雄市前金區日照中心最新動態、課程公告、健康資訊與重要公告。",
    "isPartOf": {
      "@type": "WebSite",
      "@id": "https://qianjin-daycare.com/#website"
    },
    "about": {
      "@id": "https://qianjin-daycare.com/#organization"
    },
    "breadcrumb": {
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "首頁",
          "item": "https://qianjin-daycare.com"
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "最新消息",
          "item": "https://qianjin-daycare.com/blog"
        }
      ]
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="最新消息｜前金日間照顧中心｜高雄市前金區日照中心活動資訊"
        description="前金日間照顧中心最新消息與活動花絮，掌握高雄市前金區日照中心最新動態、課程公告、健康資訊與重要公告。長照3.0失智失能照護最新資訊。"
        keywords="前金日間照顧中心最新消息,高雄日照活動,前金日照課程,長照3.0資訊,失智照護活動"
        canonical="https://qianjin-daycare.com/blog"
        lastModified="2025-07-15"
        jsonLd={blogPageSchema}
      />
      <BlogNavbar />

      <main className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <header className="text-center mb-12">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">最新消息</h1>
            <p className="text-lg text-gray-600">掌握<strong>前金日間照顧中心</strong>最新動態與活動資訊</p>
          </header>

          <div className="mb-8 flex flex-col md:flex-row gap-4 items-center justify-between">
            <div className="flex flex-wrap gap-2">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                    selectedCategory === category
                      ? 'bg-teal-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>

            <div className="relative w-full md:w-64">
              <input
                type="text"
                placeholder="搜尋文章..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
              />
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 flex items-center justify-center"></i>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-16">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-teal-600 border-t-transparent"></div>
              <p className="mt-4 text-gray-600">載入中...</p>
            </div>
          ) : error ? (
            <div className="text-center py-16">
              <i className="ri-error-warning-line text-5xl text-red-500 mb-4 w-12 h-12 flex items-center justify-center mx-auto"></i>
              <p className="text-red-600">{error}</p>
              <button
                onClick={fetchPosts}
                className="mt-4 px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap"
              >
                重新載入
              </button>
            </div>
          ) : filteredPosts.length === 0 ? (
            <div className="text-center py-16">
              <i className="ri-file-search-line text-5xl text-gray-400 mb-4 w-12 h-12 flex items-center justify-center mx-auto"></i>
              <p className="text-gray-600">找不到相關文章</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredPosts.map((post) => (
                <Link
                  key={post.id}
                  to={`/blog/${post.id}`}
                  className="group bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300"
                >
                  <div className="relative h-48 w-full overflow-hidden">
                    <img
                      src={post.image_url}
                      alt={`${post.title} - 前金日間照顧中心 ${post.category}`}
                      title={`${post.category} - ${post.title}`}
                      className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-300"
                    />
                    <div className="absolute top-4 left-4">
                      <span className="px-3 py-1 bg-teal-600 text-white text-xs font-medium rounded-full whitespace-nowrap">
                        {post.category}
                      </span>
                    </div>
                  </div>

                  <div className="p-6">
                    <div className="flex items-center gap-4 text-sm text-gray-500 mb-3">
                      <div className="flex items-center gap-1">
                        <i className="ri-calendar-line w-4 h-4 flex items-center justify-center"></i>
                        <span>{formatDate(post.date)}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <i className="ri-user-line w-4 h-4 flex items-center justify-center"></i>
                        <span>{post.author}</span>
                      </div>
                    </div>

                    <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-teal-600 transition-colors">
                      {post.title}
                    </h3>
                    <p className="text-gray-600 text-sm line-clamp-2">{post.excerpt}</p>

                    <div className="mt-4 flex items-center text-teal-600 font-medium text-sm">
                      <span className="whitespace-nowrap">閱讀更多</span>
                      <i className="ri-arrow-right-line ml-1 group-hover:translate-x-1 transition-transform w-4 h-4 flex items-center justify-center"></i>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
